/**
 * Sold-out signup form. Two different backends, on purpose:
 *
 *  - Pre-Order + signed in  -> the APP PROXY (/apps/etg/waitlist/*), fair queue
 *  - Pre-Order + guest      -> back_in_stock_subscribe (email capture) + a
 *                              "Sign in to join the queue" link (rendered in Liquid)
 *  - Not pre-order          -> back_in_stock_subscribe (restock notify)
 *
 * Why the split.
 *
 * The waitlist path used to call the guildhall Supabase RPCs directly with the
 * public anon key, passing `p_customer_id` straight from a data attribute. That
 * is forgeable: anyone could edit the DOM and join, or read a position, as
 * somebody else. Positions are the entire point of this queue, so that identity
 * now comes from Shopify. Requests go to the shop's own /apps/etg path, Shopify
 * signs them and appends a verified `logged_in_customer_id`, and the app reads
 * that instead of anything the browser said.
 *
 * The guest and restock path deliberately stays on the anon RPC. It is keyed by
 * email and has no identity to forge, so routing it through the proxy would add
 * a dependency without buying anything.
 *
 * Side effect worth knowing: the queue offset (copies spoken for by the standing
 * wholesale order before the customer line starts) is no longer read from the
 * `theme.waitlist_offset` metafield. The proxy derives it from live open orders,
 * so it can no longer be stale or forgotten on a new release.
 *
 * See shopify_management/WAITLIST.md and etg-preorder-app/app/routes/proxy.*.
 */
class BackInStockForm extends HTMLElement {
  connectedCallback() {
    // Named `fields`, not `form`, and the name is load-bearing: this must stay a
    // plain container. See the comment below and in back-in-stock-form.liquid.
    this.fields = this.querySelector('[data-back-in-stock-fields]');
    this.input = this.querySelector('[data-back-in-stock-email]');
    this.button = this.querySelector('[data-back-in-stock-submit]');
    this.messageEl = this.querySelector('[data-back-in-stock-message]');

    this.mode = this.dataset.mode || 'restock';
    this.loggedIn = this.dataset.loggedIn === 'true';
    this.isWaitlist = this.mode === 'waitlist' && this.loggedIn;

    // A click and Enter-in-the-field, rather than a form submit event.
    //
    // There is no <form> here on purpose: this snippet renders INSIDE the product
    // add-to-cart form, and a nested form is dropped by the HTML parser (see the
    // comment in back-in-stock-form.liquid). Listening for 'submit' meant listening
    // on an element that did not exist, and a type="submit" button in that position
    // posted to /cart/add.js instead, so a sold-out pre-order answered "already sold
    // out" to the one form that is supposed to work when stock is zero.
    //
    // Enter is bound explicitly because losing the form also lost the implicit
    // submit-on-Enter that people expect from a single-field email box.
    if (this.button) this.button.addEventListener('click', this.onSubmit.bind(this));
    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();   // otherwise Enter submits the enclosing cart form
        this.onSubmit(event);
      });
    }

    // Signed-in members on a pre-order: show their existing place in line.
    if (this.isWaitlist) this.checkStatus();
  }

  rpc(fn) {
    return `${this.dataset.supabaseUrl}/rest/v1/rpc/${fn}`;
  }

  /**
   * App Proxy endpoint on the shop's own domain. Must stay same-origin: Shopify
   * only signs and forwards requests made to the storefront domain, and the
   * signature is what carries the verified customer identity.
   */
  proxy(path) {
    return `${this.dataset.proxyBase || '/apps/etg'}/waitlist/${path}`;
  }

  headers() {
    return {
      'Content-Type': 'application/json',
      apikey: this.dataset.supabaseKey,
      Authorization: `Bearer ${this.dataset.supabaseKey}`,
    };
  }

  /**
   * Ask the signup-confirm edge function to send the confirmation email now.
   *
   * For the RESTOCK path this is the prompt sender, with the hourly sweep in
   * shopify_management behind it. For the WAITLIST path it is no longer either:
   * the app proxy's join route sends the confirmation before it answers, and has
   * already stamped confirmation_sent_at by the time this runs, so this finds
   * nothing to do and skips. It stays only as a third line of defence.
   *
   * Deliberately not awaited and never surfaced to the shopper: the signup itself
   * has already succeeded by this point, so a failure here must not turn it into
   * an error message.
   *
   * It IS logged, though. This call sat here silently doing nothing for weeks
   * while every confirmation fell through to the :40-past-the-hour sweep, and the
   * reason nobody found out is that the old `.catch` here discarded the failure
   * without a word. An invisible failure is the expensive kind.
   */
  sendConfirmation(payload) {
    const base = this.dataset.functionsUrl;
    if (!base) {
      console.warn('[waitlist] no data-functions-url; confirmation left to the hourly sweep');
      return;
    }
    fetch(`${base}/signup-confirm`, {
      method: 'POST',
      headers: this.headers(),
      keepalive: true, // survives the page being navigated away from straight after
      body: JSON.stringify({
        ...payload,
        title: this.dataset.productTitle,
        url: this.dataset.productUrl,
        release_date: this.dataset.releaseDate || null,
      }),
    })
      .then(async (res) => {
        // The function answers 200 for every outcome on purpose, so the body is
        // the only place a refusal shows. "already sent" is the expected answer
        // on the waitlist path now and is not worth a warning.
        if (!res.ok) {
          console.warn('[waitlist] signup-confirm returned', res.status);
          return;
        }
        const data = await res.json().catch(() => null);
        if (data && data.skipped && data.skipped !== 'no unconfirmed signup') {
          console.warn('[waitlist] signup-confirm skipped:', data.skipped);
        }
      })
      .catch((err) => {
        // Never reached Supabase at all: blocked, offline, or CORS. The hourly
        // sweep still covers the customer; this line is so we can see it.
        console.warn('[waitlist] signup-confirm did not reach Supabase:', err && err.message);
      });
  }

  async checkStatus() {
    try {
      const res = await fetch(
        `${this.proxy('status')}?product_id=${encodeURIComponent(this.dataset.productId)}`,
        { headers: { Accept: 'application/json' } },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.position) {
        if (this.fields) this.fields.hidden = true;
        this.showPosition(data.position, true);
      }
    } catch (err) {
      /* non-fatal: leave the form as-is so they can still join */
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const email = (this.input.value || '').trim();
    if (!email || !this.input.checkValidity()) {
      // Without an enclosing form, `required` and type="email" no longer gate a
      // submit on their own, and a silent return reads as a dead button. The
      // constraint API still works on a standalone input, so ask for the same
      // native bubble the browser used to show.
      this.input.reportValidity();
      return;
    }

    this.button.setAttribute('disabled', 'disabled');
    this.setMessage('', '');

    try {
      if (this.isWaitlist) {
        // No customer id and no offset in this payload on purpose. Shopify
        // appends the verified customer, and the app derives the offset from
        // live open orders; anything this form claimed about either would be
        // taken from a page the shopper can edit.
        const res = await fetch(this.proxy('join'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            product_id: Number(this.dataset.productId),
            variant_id: this.dataset.variantId ? Number(this.dataset.variantId) : null,
            quantity: 1,
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        if (this.fields) this.fields.hidden = true;
        this.showPosition(data.position, data.already_joined);
        // Only on a genuinely new join: re-joining has already been confirmed once.
        // The customer id comes back from the proxy rather than the DOM, since
        // the DOM no longer carries one.
        if (!data.already_joined && data.customer_id) {
          this.sendConfirmation({
            table: 'waitlist',
            product_id: Number(this.dataset.productId),
            customer_id: Number(data.customer_id),
          });
        }
      } else {
        // Restock list, or a pre-order guest email fallback.
        const res = await fetch(this.rpc('back_in_stock_subscribe'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({
            p_product_id: Number(this.dataset.productId),
            p_variant_id: this.dataset.variantId ? Number(this.dataset.variantId) : null,
            p_email: email,
            p_customer_id: this.dataset.customerId ? Number(this.dataset.customerId) : null,
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        if (this.fields) this.fields.hidden = true;
        const msg = this.mode === 'waitlist' ? this.dataset.guestSuccess : this.dataset.restockSuccess;
        this.setMessage(msg, 'success');
        this.sendConfirmation({
          table: 'back_in_stock',
          product_id: Number(this.dataset.productId),
          email,
        });
      }
    } catch (err) {
      this.button.removeAttribute('disabled');
      this.setMessage(this.dataset.errorText, 'error');
    }
  }

  showPosition(position, already) {
    const tpl = already ? this.dataset.waitlistAlready : this.dataset.waitlistSuccess;
    this.setMessage((tpl || '').replace('[POSITION]', position), 'success');
  }

  setMessage(text, kind) {
    this.messageEl.textContent = text;
    this.messageEl.hidden = !text;
    this.messageEl.classList.toggle('back-in-stock-form__message--error', kind === 'error');
    this.messageEl.classList.toggle('back-in-stock-form__message--success', kind === 'success');
  }
}

customElements.define('back-in-stock-form', BackInStockForm);
